import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputSwitch } from 'primereact/inputswitch';
import { Skeleton } from 'primereact/skeleton';
import { useIntl } from 'react-intl';
import { useRpeTab } from '../../hooks/coach/useRpeTab';
import { useRpeMethodDialog } from '../../hooks/dialogs/useRpeMethodDialog';
import { useRpeAssignmentDialog } from '../../hooks/dialogs/useRpeAssignmentDialog';
import { RpeMethodDialog } from '../dialogs/RpeMethodDialog';
import { RpeAssignmentDialog } from '../dialogs/RpeAssignmentDialog';
import { useToast } from '../../contexts/ToastContext';

export function RpeTab() {
  const intl = useIntl();
  const { showToast } = useToast();
  const state = useRpeTab();
  const rpeMethodDialog = useRpeMethodDialog();
  const rpeAssignDialog = useRpeAssignmentDialog();

  return (
    <div className="p-3">
      <div className="flex gap-2 mb-3 justify-content-end">
        <Button
          label={intl.formatMessage({ id: 'coach.createRpeMethod' })}
          icon="pi pi-plus-circle"
          onClick={() => rpeMethodDialog.openCreate()}
          className="p-button-primary"
        />
        {/*
        <Button
          label={intl.formatMessage({ id: 'coach.assignRpe' })}
          icon="pi pi-link"
          onClick={() => {
            state.setSelectedType(null);
            state.setSelectedTarget(null);
            state.setSelectedRpe(null);
            rpeAssignDialog.open();
          }}
          className="p-button-outlined"
          disabled={state.rpeMethods.length === 0}
        />
        */}
      </div>

      <Card title={intl.formatMessage({ id: 'coach.rpeMethods' })}>
        {state.loading ? (
          <div className="grid">
            {[1, 2, 3].map((i) => (
              // El skeleton espeja la estructura real de la card para que no
              // haya salto al cargar: título + badge "Global", la línea del
              // rango, la fila del switch de "método por defecto" y las
              // acciones. Antes eran tres barras apiladas sueltas y dos
              // círculos, que ya no se parecía a nada de lo que se renderiza.
              <div key={i} className="col-12 md:col-6 lg:col-4">
                <div className="surface-card shadow-2 border-round p-4">
                  {/* Título + badge */}
                  <div className="flex align-items-center gap-2 mb-3">
                    <Skeleton width="45%" height="1.5rem" />
                    <Skeleton width="3.5rem" height="1.15rem" borderRadius="9999px" />
                  </div>

                  {/* Rango: icono + "min - max (paso: n)" */}
                  <div className="flex align-items-center gap-2 mb-3">
                    <Skeleton shape="circle" size="1rem" />
                    <Skeleton width="60%" height="1rem" />
                  </div>

                  {/* Fila del switch de método por defecto */}
                  <div className="flex align-items-center gap-2 mb-3">
                    <Skeleton width="2.6rem" height="1.5rem" borderRadius="9999px" />
                    <Skeleton width="9rem" height="1rem" />
                  </div>

                  {/* Acciones */}
                  <div className="flex gap-2 justify-content-end">
                    <Skeleton shape="circle" size="2rem" />
                    <Skeleton shape="circle" size="2rem" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : state.rpeMethods.length > 0 ? (
          <div className="grid">
            {state.rpeMethods.map((rpe) => {
              const isGlobal = rpe.isGlobal === true;
              return (
                <div key={rpe.id} className="col-12 md:col-6 lg:col-4">
                  <div className="surface-card shadow-2 border-round p-4">
                    <div className="flex align-items-center gap-2 mb-3">
                      <h3 className="text-xl font-bold m-0">{rpe.name}</h3>
                      {isGlobal && (
                        <span
                          className="text-xs"
                          style={{
                            background: 'rgba(163, 230, 53, 0.18)',
                            color: '#4d7c0f',
                            padding: '2px 8px',
                            borderRadius: 9999,
                            fontWeight: 600,
                            whiteSpace: 'nowrap'
                          }}
                          title={intl.formatMessage({
                            id: 'rpe.globalBadge.tooltip',
                            defaultMessage: 'Método global pre-cargado (no editable)'
                          })}
                        >
                          {intl.formatMessage({
                            id: 'rpe.globalBadge',
                            defaultMessage: 'Global'
                          })}
                        </span>
                      )}
                    </div>
                    <div className="flex align-items-start flex-column gap-2 mb-1 text-600">
                      <p className="flex align-items-center gap-2">
                        <i className="pi pi-sliders-h"></i>
                        {rpe.minValue} - {rpe.maxValue} ({intl.formatMessage({ id: 'rpe.step' })}: {rpe.step})
                      </p>
                      <div className="flex align-items-center gap-2">
                        {/* #14: el coach elige cuál es SU predeterminado, y puede
                            ser uno global. `isCoachDefault` lo resuelve el backend
                            (elección del coach -> propio con isDefault -> global
                            con isDefault); `isDefault` es la columna compartida y
                            ya no se usa acá. */}
                        <InputSwitch
                          checked={rpe.isCoachDefault === true}
                          onChange={() => {
                            if (!rpe.isCoachDefault) {
                              state.setDefaultMethod(rpe.id);
                            }
                          }}
                          disabled={rpe.isCoachDefault === true}
                          tooltip={
                            rpe.isCoachDefault === true
                              ? undefined
                              : intl.formatMessage({
                                  id: 'rpe.setAsDefault',
                                  defaultMessage: 'Usar este método por defecto'
                                })
                          }
                        />
                        <span>{intl.formatMessage({ id: 'rpe.isDefault' })}</span>
                      </div>
                    </div>
                    <div className="flex gap-2 justify-content-end">
                      {isGlobal ? (
                        <span className="text-xs" style={{ color: 'var(--ios-text-tertiary)', alignSelf: 'center' }}>
                          {intl.formatMessage({
                            id: 'rpe.globalBadge.actionsLocked',
                            defaultMessage: 'Solo lectura'
                          })}
                        </span>
                      ) : (
                        <>
                          <Button
                            icon="pi pi-pencil"
                            className="p-button-text p-button-rounded"
                            onClick={() => rpeMethodDialog.openEdit(rpe as any)}
                            tooltip={intl.formatMessage({ id: 'common.edit' })}
                            tooltipOptions={{ position: 'top' }}
                          />
                          <Button
                            icon="pi pi-trash"
                            className="p-button-text p-button-rounded p-button-danger"
                            onClick={() => state.deleteMethod(rpe.id)}
                            tooltip={intl.formatMessage({ id: 'common.delete' })}
                            tooltipOptions={{ position: 'top' }}
                          />
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-column align-items-center justify-content-center p-4 text-center">
            <i
              className="pi pi-info-circle"
              style={{
                fontSize: '2rem',
                color: 'var(--ios-text-tertiary)',
                marginBottom: '0.5rem'
              }}
            />
            <p style={{ color: 'var(--ios-text-secondary)', margin: 0 }}>
              {intl.formatMessage({
                id: 'coach.noRpeMethods',
                defaultMessage: 'No hay métodos de medición de carga subjetiva'
              })}
            </p>
          </div>
        )}
      </Card>

      {(state.rpeAssignments.length > 0 || state.loading) && (
        <Card className="mt-3" title={intl.formatMessage({ id: 'coach.rpeAssignments' })}>
          <DataTable value={state.rpeAssignments} stripedRows paginator rows={10} dataKey="id" loading={state.loading}>
            <Column
              field="rpeId"
              header={intl.formatMessage({ id: 'rpe.method' })}
              body={(row) => state.getRpeNameById(row)}
              style={{ minWidth: '200px' }}
            />
            <Column
              field="targetType"
              header={intl.formatMessage({ id: 'rpe.targetType' })}
              body={(row) => (
                <span className="inline-flex align-items-center gap-2 bg-primary-100 text-primary-900 px-2 py-1 border-round">
                  <i className={`pi ${row.targetType === 'exercise' ? 'pi-heart' : 'pi-user'}`}></i>
                  {state.formatTargetType(row.targetType)}
                </span>
              )}
              style={{ minWidth: '150px' }}
            />
            <Column
              field="targetName"
              header={intl.formatMessage({ id: 'rpe.targetName' })}
              style={{ minWidth: '200px' }}
            />
            <Column
              field="createdAt"
              header={intl.formatMessage({ id: 'common.createdAt' })}
              style={{ minWidth: '150px' }}
            />
          </DataTable>
        </Card>
      )}

      <RpeMethodDialog
        visible={rpeMethodDialog.visible}
        loading={rpeMethodDialog.loading}
        mode={rpeMethodDialog.mode}
        form={rpeMethodDialog.form}
        onHide={rpeMethodDialog.close}
        onChange={(patch) => rpeMethodDialog.setField(Object.keys(patch)[0] as any, Object.values(patch)[0] as any)}
        onGenerateValues={rpeMethodDialog.generateValues}
        onAddValue={rpeMethodDialog.addValueMeta}
        onUpdateValue={rpeMethodDialog.updateValueMeta}
        onRemoveValue={rpeMethodDialog.removeValueMeta}
        onSave={async () => {
          const res = await rpeMethodDialog.save();
          if (res.success) {
            state.loadRpeData();
            const msgKey =
              rpeMethodDialog.mode === 'create' ? 'coach.rpe.success.created' : 'coach.rpe.success.updated';
            showToast('success', intl.formatMessage({ id: 'common.success' }), intl.formatMessage({ id: msgKey }));
          }
          return res;
        }}
      />

      <RpeAssignmentDialog
        visible={rpeAssignDialog.visible}
        loading={rpeAssignDialog.loading}
        types={state.typeOptions}
        selectedType={state.selectedType}
        onChangeType={(v) => {
          state.setSelectedType(v);
          rpeAssignDialog.setType(v);
        }}
        targets={state.targets}
        selectedTargetId={state.selectedTarget}
        onChangeTargetId={(id) => {
          state.setSelectedTarget(id);
          rpeAssignDialog.setTargetId(id);
        }}
        rpeMethods={state.rpeMethods.map((r) => ({ label: r.name, value: r.id }))}
        selectedRpeMethodId={state.selectedRpe}
        onChangeRpeMethodId={(id) => {
          state.setSelectedRpe(id);
          rpeAssignDialog.setRpeMethodId(id);
        }}
        onHide={rpeAssignDialog.close}
        onAssign={async () => {
          const res = await rpeAssignDialog.assign();
          if (res.success) state.loadRpeData();
          return res;
        }}
      />
    </div>
  );
}
